import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useQueries } from '@tanstack/react-query';
import { graphDataAtom, IEcoDriveState } from '../EcoDriveState';
import EcoDriveChart from '../EcoDriveChart';
import Header from './Header';
import {
  getGraphGdpr,
  getPointsData,
  getPointsCount,
  getVehicleData,
  getViolationsData,
  GRAPH_LIMIT,
  hadleGraphDataAccordingToLimit,
  IGraphData,
  IGraphResponseData,
} from '../../GraphService';
import OnlineViolationsTable from './OnlineViolationsTable';
import RideJustEnded from '../../RideJustEnded';
import GdprIcon from '../../GdprIcon';
import usePrevious from '../../../../hooks/usePrevious';
import { IVehicleGdprDataAllowed } from '../../../Vehicles/types';
import PositionDetails from '../../PositionsDetails';
import { graphCurrentPointsDataAtom, IGraphCurrentPointsData } from '../../GraphState';
import {
  isVehicleGdprDataAllowedAtom,
  vehicleByIdSelector,
} from '../../../../store/recoil/vehicles';

interface IProps {
  vehicleId: number;
  responseData: IGraphResponseData[];
  isCompressed: boolean;
  children?: JSX.Element;
  loadRide: () => void;
  handleEndRideDateAndAddress: (address: string) => void;
  isDetail: boolean;
}

function Driving(props: IProps) {
  const { formatMessage: f } = useIntl();

  const [ignitionOnFirstTs, setIgnitionOnFirstTs] = React.useState<string | null>('');
  const allEcoDriveData = useRecoilValue<{ [name: string]: IEcoDriveState }>(graphDataAtom);
  const setGraphCurrentPointsData = useSetRecoilState<IGraphCurrentPointsData[] | null>(
    graphCurrentPointsDataAtom
  );
  const [isRideJustEnded, setIsRideJustEnded] = React.useState(false);
  const [pointsCount, setPointsCount] = React.useState(0);
  const [ecoDriveSocketData, setEcoDriveSocketData] = React.useState<IEcoDriveState | null>(null);
  const [violationSocketData, setViolationSocketData] = React.useState<IEcoDriveState | null>(null);
  const [graphData, setGraphData] = React.useState<IGraphData>({
    xLabels: [],
    yLabels: [],
    tooltips: {},
    minLabels: [],
    maxLabels: [],
  });
  const [tableData, setTableData] = React.useState<Record<string, any>[]>([]);
  const [onlineEcoScore, setOnlineEcoScore] = React.useState<number | null>(
    props.vehicleId &&
      ecoDriveSocketData &&
      ecoDriveSocketData[props.vehicleId] &&
      ecoDriveSocketData[props.vehicleId].ecoDriveScore
      ? Number(ecoDriveSocketData[props.vehicleId].ecoDriveScore.toFixed(1))
      : null
  );
  const [isNotAllowedByGdpr, setIsNotAllowedByGdpr] = React.useState(false);
  const vehicle = useRecoilValue(vehicleByIdSelector(props.vehicleId));
  const previousDrivingMode = usePrevious(vehicle?.drivingMode, { jsonParse: false });
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );

  React.useEffect(() => {
    if (
      Object.keys(allEcoDriveData).length > 0 &&
      allEcoDriveData[props.vehicleId] &&
      allEcoDriveData[props.vehicleId].timestamp
    ) {
      if (allEcoDriveData[props.vehicleId] && allEcoDriveData[props.vehicleId].ecoDriveScore) {
        let newScore: number | null = null;
        if (
          props.vehicleId &&
          allEcoDriveData &&
          allEcoDriveData[props.vehicleId] &&
          allEcoDriveData[props.vehicleId].ecoDriveScore
        ) {
          newScore = allEcoDriveData[props.vehicleId].ecoDriveScore;
          newScore = Number(Number(newScore).toFixed(1));
        }

        setOnlineEcoScore(newScore ? Number(newScore) : null);
      }

      setEcoDriveSocketData(allEcoDriveData[props.vehicleId]);
    }
    if (
      Object.keys(allEcoDriveData).length > 0 &&
      allEcoDriveData[`violation-${props.vehicleId}`] &&
      allEcoDriveData[`violation-${props.vehicleId}`].timestamp
    ) {
      setViolationSocketData(allEcoDriveData[`violation-${props.vehicleId}`]);
    }
  }, [allEcoDriveData, props.vehicleId]);

  const getData = () => {
    const xLabels: string[] = [];
    const yLabels: (number | null)[] = [];
    const minLabels: string[] = [];
    const maxLabels: string[] = [];
    const tooltips = {};
    // const violations: (number | null)[] = [];
    if (props.responseData) {
      let distanceCummulative = 0;

      setPointsCount(getPointsCount(props.responseData, ignitionOnFirstTs));
      props.responseData.forEach((i) => {
        if (i.distance !== undefined) {
          distanceCummulative = Number((distanceCummulative + Number(i.distance)).toFixed(3));
        }
        if (!xLabels.includes(i.timestamp)) {
          xLabels.push(i.timestamp);
          yLabels.push(i.value);
          minLabels.push(i.min);
          maxLabels.push(i.max);
          tooltips[i.timestamp] = {
            address: i.address || `${i.latitude}, ${i.longitude}`,
            violations: i.violations,
            code: i.code,
          };
          if (i.distance !== undefined) {
            tooltips[i.timestamp].distance = `${distanceCummulative} km`;
          }
          // if (i.violations && i.violations.length > 0) {
          //   violations.push(6);
          // } else {
          //   violations.push(0);
          // }
        } else if (
          new Date(xLabels[xLabels.length - 1]) === new Date(i.timestamp) &&
          i.violations &&
          i.violations.length > 0
        ) {
          xLabels[xLabels.length - 1] = i.timestamp;
          yLabels[xLabels.length - 1] = i.value;
          tooltips[i.timestamp] = {
            address: i.address || `${i.latitude}, ${i.longitude}`,
            violations: i.violations,
            code: i.code,
          };
          if (i.distance !== undefined) {
            tooltips[i.timestamp].distance = `${distanceCummulative} km`;
          }
          // violations[xLabels.length - 1] = 6;
        }
      });
    }
    setGraphData({
      xLabels,
      yLabels,
      minLabels,
      maxLabels,
      tooltips,
    });
  };

  const handleViolationsData = async () => {
    const response = await getViolationsData(props.vehicleId);
    if (response) {
      const { records } = response;
      if (records) {
        records.reverse();
        setTableData(records);
      }
    }
  };

  React.useEffect(() => {
    if (graphData?.xLabels && graphData.xLabels.length > 0) {
      const pointData = getPointsData(graphData);
      setGraphCurrentPointsData(pointData);
    }
  }, [graphData, setGraphCurrentPointsData]);

  React.useEffect(() => {
    if (props.responseData && ignitionOnFirstTs !== '') {
      getData();
      setIsRideJustEnded(false);
      if (props.isDetail) {
        handleViolationsData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.responseData, ignitionOnFirstTs]);

  const handleVehicleData = async () => {
    const res = await getVehicleData(props.vehicleId);
    if (res !== false) {
      setIgnitionOnFirstTs(res);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['graphVehicleData', props.vehicleId],
        queryFn: () => handleVehicleData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  // const getDistanceNumber = (value: string) => {
  //   const values = value.split(' km');
  //   return Number(Number(values[0]).toFixed(3));
  // };

  // const getCummulativeDistance = React.useCallback(
  //   (oldDistanceInKm: string, newDistance: string | null) => {
  //     const prevDistance = oldDistanceInKm ? getDistanceNumber(oldDistanceInKm) : 0;
  //     const nextDistance = newDistance ? Number(newDistance) : 0;
  //     const cummulativeDistance = prevDistance + nextDistance;
  //     return cummulativeDistance ? `${cummulativeDistance} km` : '';
  //   },
  //   []
  // );

  React.useEffect(() => {
    if (
      !isRideJustEnded &&
      props.vehicleId &&
      ecoDriveSocketData &&
      !graphData.xLabels.includes(ecoDriveSocketData.timestamp) &&
      (new Date(graphData.xLabels[graphData.xLabels.length - 1]) <
        new Date(ecoDriveSocketData.timestamp) ||
        graphData.xLabels.length === 0)
    ) {
      if (ecoDriveSocketData.ecoDriveScore !== null) {
        let tempGraphData = { ...graphData };
        tempGraphData.xLabels.push(ecoDriveSocketData.timestamp);
        tempGraphData.yLabels.push(ecoDriveSocketData.ecoDriveScore);
        tempGraphData.tooltips[ecoDriveSocketData.timestamp] = {
          address: ecoDriveSocketData.address,
          code: ecoDriveSocketData.code,
        };
        // if (tempGraphData.violations) {
        //   tempGraphData.violations.push(0);
        // }
        tempGraphData = hadleGraphDataAccordingToLimit(tempGraphData, pointsCount + 1);

        setGraphData(tempGraphData);
        setPointsCount(pointsCount + 1);
      }
    }
  }, [props.vehicleId, graphData, ecoDriveSocketData, isRideJustEnded, pointsCount]);

  React.useEffect(() => {
    if (
      !isRideJustEnded &&
      props.vehicleId &&
      violationSocketData &&
      (new Date(graphData.xLabels[graphData.xLabels.length - 1]) <
        new Date(violationSocketData.timestamp) ||
        // graphData.violations &&
        (new Date(graphData.xLabels[graphData.xLabels.length - 1]) ===
          new Date(violationSocketData.timestamp) &&
          !graphData.tooltips[violationSocketData.timestamp]?.violations) ||
        // graphData.violations[graphData.violations.length - 1] !== 6
        graphData.xLabels.length === 0)
    ) {
      if (violationSocketData.ecoDriveScore !== null) {
        const violations = {
          duration: violationSocketData.duration,
          maxValue: violationSocketData.maxValue,
          type: violationSocketData.type,
        };
        const tableViolation = {
          duration: violationSocketData.duration,
          maxValue: { value: violationSocketData.maxValue, tooltip: violationSocketData.tooltip },
          type: violationSocketData.type,
          map: violationSocketData.map,
          address: violationSocketData.address,
          links: { map: violationSocketData.link },
          timestamp: violationSocketData.timestamp,
        };

        // table
        if (tableData.length > 9) {
          const newTableData: Record<string, any>[] = [];
          tableData.forEach((i: Record<string, any>, key: number) => {
            if (key > 0) {
              newTableData.push(i);
            }
          });
          newTableData.push(tableViolation);
          setTableData(newTableData);
        } else {
          tableData.push(tableViolation);
        }

        // graph
        let tempGraphData = { ...graphData };
        tempGraphData.xLabels.push(violationSocketData.timestamp);
        tempGraphData.yLabels.push(violationSocketData.ecoDriveScore);
        tempGraphData.tooltips[violationSocketData.timestamp] = {
          address: violationSocketData.address,
          violations: [violations],
        };
        // if (tempGraphData.violations) {
        //   tempGraphData.violations.push(6);
        // }
        tempGraphData = hadleGraphDataAccordingToLimit(tempGraphData, pointsCount + 1);

        setGraphData(tempGraphData);
        setPointsCount(pointsCount + 1);
      }
    }
  }, [props.vehicleId, graphData, violationSocketData, isRideJustEnded, pointsCount, tableData]);

  React.useEffect(() => {
    if (
      !isRideJustEnded &&
      vehicle?.drivingMode &&
      previousDrivingMode &&
      vehicle?.drivingMode !== previousDrivingMode
    ) {
      if (vehicle?.drivingMode === 'private') {
        if (!getGraphGdpr(isVehicleGdprDataAllowed, vehicle.id, f)) {
          setIsNotAllowedByGdpr(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousDrivingMode, vehicle?.drivingMode]);

  const handleRideJustEnded = () => {
    setIsRideJustEnded(true);
  };

  const renderLimitAlert = () => {
    return !props.isCompressed && pointsCount > GRAPH_LIMIT ? (
      <div style={{ padding: props.isDetail ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.limitAlert.title' }),
            message: f({ id: 'graph.graphLimit.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  const renderEcoDriveChart = () => {
    return !isNotAllowedByGdpr ? (
      <EcoDriveChart
        graphData={graphData}
        noAnimation="animation"
        isCompressed={props.isCompressed}
        isOnline
        isDetail={props.isDetail}
      />
    ) : (
      <GdprIcon />
    );
  };

  return (
    <div>
      <RideJustEnded
        vehicleId={props.vehicleId}
        handleEndRideDateAndAddress={props.handleEndRideDateAndAddress}
        loadData={props.loadRide}
        pointsCount={pointsCount}
        isRideJustEnded={handleRideJustEnded}
      />
      {props.isDetail ? (
        <div data-cy="reports-form-component">
          <Header vehicleId={props.vehicleId} onlineEcoScore={onlineEcoScore} />
          <CmdBox id="tripsOverview.section.graph" title={f({ id: 'tripsOverview.section.graph' })}>
            {props.children}
            {renderLimitAlert()}
            <div style={{ marginTop: '10px', padding: '20px 0px 20px 25px' }}>
              {renderEcoDriveChart()}
            </div>
          </CmdBox>
          <PositionDetails vehicleId={props.vehicleId} positions={props.responseData} />
          <OnlineViolationsTable tableData={tableData} vehicleId={String(props.vehicleId)} />
        </div>
      ) : (
        <div>
          {renderLimitAlert()}
          {renderEcoDriveChart()}
        </div>
      )}
    </div>
  );
}

export default React.memo(Driving);
